import React, { useRef, useState, useEffect } from "react";
import { Upload, FileVideo, Loader2, Check, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fehlerText, createSequence, uploadVideo, fetchThumbnail, fetchCameraProfiles, pruefeVideoName } from "@/lib/api";
import { namensHinweis, schalterAn, videoName } from "@/lib/bodenklicks";
import { TID } from "@/lib/testIds";
import { FORMATS, GENDERS } from "@/lib/gameConfig";
import {
  DEFAULT_PROFILE_ID,
  getProfile,
  listProfilesForFormat,
} from "@/lib/fieldSpecs";

const BODENKLICK_AN = schalterAn();   // 🆕 A60 R4: Schalter der Klickstrecke
const MAX_UPLOAD_GB = 10;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_GB * 1024 * 1024 * 1024;

/**
 * Step 1 — file pick triggers a Direct-to-B2 multipart upload immediately.
 * Game-config dropdowns are set FIRST and lock once the upload starts.
 * The browser sends chunks straight to Backblaze (no FastAPI byte-path),
 * so very large iPhone/HEVC files no longer pressure the backend pod.
 * After upload the frontend pre-warms a codec-safe ffmpeg thumbnail so
 * Step 2 (calibration) opens with a still already decoded.
 */
export default function Step1Upload({ onComplete }) {
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const [file, setFile] = useState(null);
  const [format, setFormat] = useState("Outdoor");
  const [gender, setGender] = useState("Open");
  // 🆕 v8c.11 (2026-02-28) · Wired DIRECTLY to /app/shared/field_specs.json
  // via lib/fieldSpecs.js — single source of truth across the entire
  // stack. Canonical IDs: Adult_Outdoor, U17_Outdoor, U14_Outdoor,
  // Indoor, Beach. The four numeric dims are pre-filled from the
  // chosen profile but stay editable — operator can dial in
  // non-standard tournament dimensions while keeping the divisional
  // label intact.
  const [fieldProfileId, setFieldProfileId] = useState(DEFAULT_PROFILE_ID);
  // 🆕 Phase 4 Schritt 1 · Kamera-Profil (PnL-Intrinsik-Startwert).
  // Registry: GET /api/camera-profiles ← /app/shared/camera_profiles.json.
  // Kaskade im Backend: EXIF-FOV > dieses Profil > 70°-Fallback.
  const [cameraProfiles, setCameraProfiles] = useState([]);
  const [cameraProfileId, setCameraProfileId] = useState("default");
  // 🆕 FOV-Freitext: manuelle Gradzahl schlägt Profil UND EXIF
  const [fovOverride, setFovOverride] = useState("");
  // 🆕 Phase 4 · Fremd-Mittellinie sichtbar → schaltet im Anker-
  // Sammelmodus die Zeichen-Klasse "Mittellinie" (M) frei.
  const [auxMidline, setAuxMidline] = useState(false);
  useEffect(() => {
    fetchCameraProfiles()
      .then((d) => setCameraProfiles(d.profiles || []))
      .catch(() => setCameraProfiles([{ id: "default", label: "Generic Wide (Default · 70°)", fov_deg: 70.0 }]));
  }, []);
  const [fieldDims, setFieldDims] = useState(() => {
    const p = getProfile(DEFAULT_PROFILE_ID);
    return {
      field_length_m: p.field_length_m,
      field_width_m: p.field_width_m,
      endzone_depth_m: p.endzone_depth_m,
      brick_distance_m: p.brick_distance_m,
    };
  });
  // Snap profile + dimensions when the operator changes Format so
  // we never end up with an "Indoor" sequence carrying 100×37 dims.
  // We keep the user's existing profile id IF it's still valid for
  // the new format, otherwise jump to the first profile of that fmt.
  useEffect(() => {
    const candidates = listProfilesForFormat(format);
    if (!candidates.length) return;
    const current = candidates.find((p) => p.id === fieldProfileId);
    const next = current || candidates[0];
    if (next.id !== fieldProfileId) {
      setFieldProfileId(next.id);
      setFieldDims({
        field_length_m: next.field_length_m,
        field_width_m: next.field_width_m,
        endzone_depth_m: next.endzone_depth_m,
        brick_distance_m: next.brick_distance_m,
      });
    }
  }, [format, fieldProfileId]);
  const applyProfile = (pid) => {
    const p = getProfile(pid);
    setFieldProfileId(pid);
    setFieldDims({
      field_length_m: p.field_length_m,
      field_width_m: p.field_width_m,
      endzone_depth_m: p.endzone_depth_m,
      brick_distance_m: p.brick_distance_m,
    });
  };
  const updateDim = (key, raw) => {
    const v = parseFloat(raw);
    if (Number.isNaN(v) || v < 0) return;
    setFieldDims((d) => ({ ...d, [key]: v }));
  };

  const [phase, setPhase] = useState("idle"); // idle | uploading | thumbnailing | ready | error
  // 🆕 04.09. · Der GRUND eines Fehlschlags bleibt sichtbar stehen —
  // vorher stand er nur im Toast (weg nach Sekunden) und in der Konsole,
  // und der Nutzer sah bloß „Upload failed" ohne jede Diagnose.
  const [uploadFehler, setUploadFehler] = useState("");
  const [progress, setProgress] = useState(0);
  const [sequence, setSequence] = useState(null);
  const [thumbBlobUrl, setThumbBlobUrl] = useState(null);
  const [namensHinweisText, setNamensHinweisText] = useState(null);   // 🆕 A60 R4

  // Clean up object URL on unmount / replace.
  useEffect(() => () => { if (thumbBlobUrl) URL.revokeObjectURL(thumbBlobUrl); }, [thumbBlobUrl]);

  const reset = () => {
    // Cancel any in-flight chunks + tell B2 to release staged parts.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* noop */ }
      abortRef.current = null;
    }
    if (thumbBlobUrl) URL.revokeObjectURL(thumbBlobUrl);
    setFile(null);
    setNamensHinweisText(null);
    setSequence(null);
    setUploadFehler("");
    setThumbBlobUrl(null);
    setProgress(0);
    setPhase("idle");
  };

  const startBackgroundUpload = async (f) => {
    setPhase("uploading");
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const seq = await createSequence({
        name: f.name.replace(/\.[^.]+$/, ""),
        game_config: {
          format, gender,
          field_profile_id: fieldProfileId,
          camera_profile_id: cameraProfileId,
          camera_fov_override_deg:
            fovOverride !== "" && !Number.isNaN(parseFloat(fovOverride))
              ? parseFloat(fovOverride)
              : null,
          aux_midline_visible: auxMidline,
          ...fieldDims,
        },
      });
      const meta = await uploadVideo(seq.id, f, setProgress, controller.signal);
      const full = { ...seq, ...meta };
      setSequence(full);

      // Pre-warm codec-safe thumbnail.
      setPhase("thumbnailing");
      try {
        const blob = await fetchThumbnail(seq.id, 2);
        setThumbBlobUrl(URL.createObjectURL(blob));
      } catch (thErr) {
        // Non-blocking — Step 2 will fetch on demand.
        console.warn("thumbnail pre-warm failed:", thErr);
      }
      setPhase("ready");
      toast.success(`Uploaded ${f.name} · ${meta.duration_s.toFixed(1)}s`);
    } catch (err) {
      if (err?.name === "AbortError") {
        // Caller already reset the UI state.
        return;
      }
      console.error(err);
      const grund = fehlerText(err) || err?.message || "Upload failed";
      setUploadFehler(grund);
      toast.error(grund);
      setPhase("error");
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const handleFile = (f) => {
    if (!f) return;
    if (phase !== "idle" && phase !== "error") {
      toast.error("An upload is already in progress — replace by resetting first.");
      return;
    }
    if (f.size > MAX_UPLOAD_BYTES) {
      toast.error(`File exceeds ${MAX_UPLOAD_GB} GB limit`);
      return;
    }
    if (!/\.(mp4|mov|m4v|webm)$/i.test(f.name)) {
      toast.error("Only .mp4 / .mov / .webm supported");
      return;
    }
    setFile(f);
    // 🆕 Auftrag 60, R4 · nur bei Schalter an: Namensregel abfragen, Hinweis bei Form „offen“ (Upload läuft unverändert).
    if (BODENKLICK_AN) {
      setNamensHinweisText(null);
      pruefeVideoName(videoName(f.name)).then((r) => setNamensHinweisText(namensHinweis(r))).catch(() => {});
    }
    startBackgroundUpload(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const goToCalibration = () => {
    if (!sequence) return;
    onComplete(sequence.id, sequence);
  };

  const configLocked = phase !== "idle" && phase !== "error";
  const canPickAnother = phase === "idle" || phase === "error";

  return (
    <div data-testid={TID.wizardStep(1)} className="space-y-6">
      {/* Game Config — set BEFORE picking the file. Locks once upload starts. */}
      <div className="border border-border bg-card rounded-sm p-5">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
          Step 1 · Game Configuration
        </div>
        <h3 className="font-display font-black uppercase text-xl tracking-wider mb-4">
          Match Setup {configLocked && <span className="text-primary text-sm font-mono ml-2">· locked</span>}
        </h3>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Format</Label>
            <Select value={format} onValueChange={setFormat} disabled={configLocked}>
              <SelectTrigger data-testid={TID.cfgFormat} className="mt-1.5 bg-secondary border-border focus:border-primary">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Division</Label>
            <Select value={gender} onValueChange={setGender} disabled={configLocked}>
              <SelectTrigger data-testid={TID.cfgGender} className="mt-1.5 bg-secondary border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {gender === "Mixed" && (
          <div className="mt-4 bg-accent/40 border border-primary/30 rounded-sm px-3 py-2.5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">
              Mixed — split asked per sequence
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              The on-field gender ratio (e.g. 3M/2W) changes from point to point. You&apos;ll set it per sequence in <span className="text-foreground font-semibold">Step 4 · Labeling</span>.
            </div>
          </div>
        )}

        {/* 🆕 2026-02-28 v7 · Field-Profile + editable dimensions.
            The profile dropdown narrows by Format above. Picking
            a profile snaps the four numeric inputs; the inputs stay
            editable so an admin can dial in non-standard tournament
            dimensions while keeping the divisional label intact. */}
        <div className="mt-5 border-t border-border pt-4">
          <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Field Profile · Division
          </Label>
          <Select value={fieldProfileId} onValueChange={applyProfile} disabled={configLocked}>
            <SelectTrigger
              data-testid="cfg-field-profile"
              className="mt-1.5 bg-secondary border-border focus:border-primary"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {listProfilesForFormat(format).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.display_de} · {p.field_length_m}×{p.field_width_m} m
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[10px] text-muted-foreground mt-1.5 font-mono uppercase tracking-widest">
            {getProfile(fieldProfileId)?.description || ""} · editable below
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: "field_length_m", label: "Length (m)" },
              { key: "field_width_m", label: "Width (m)" },
              { key: "endzone_depth_m", label: "Endzone (m)" },
              { key: "brick_distance_m", label: "Brick (m)" },
            ].map(({ key, label }) => (
              <div key={key}>
                <Label className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                  {label}
                </Label>
                <input
                  data-testid={`cfg-dim-${key}`}
                  type="number"
                  step="0.1"
                  min="0"
                  value={fieldDims[key]}
                  onChange={(e) => updateDim(key, e.target.value)}
                  disabled={configLocked}
                  className="mt-1 w-full bg-secondary border border-border rounded-sm px-2 py-1.5 text-sm font-mono focus:border-primary focus:outline-none disabled:opacity-50"
                />
              </div>
            ))}
          </div>
        </div>
        {/* 🆕 Phase 4 · Kamera-Profil (FOV-Startwert für den PnL-Solver).
            EXIF-Daten aus dem Video überschreiben diese Wahl automatisch;
            refine=True optimiert die Brennweite ohnehin live nach. */}
        <div className="mt-5 border-t border-border pt-4">
          <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Kamera-Profil · Objektiv-FOV
          </Label>
          <Select value={cameraProfileId} onValueChange={setCameraProfileId} disabled={configLocked}>
            <SelectTrigger
              data-testid="cfg-camera-profile"
              className="mt-1.5 bg-secondary border-border focus:border-primary"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {cameraProfiles.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`cfg-camera-profile-opt-${p.id}`}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[10px] text-muted-foreground mt-1.5 font-mono uppercase tracking-widest">
            Startwert der Brennweiten-Optimierung · EXIF-Daten im Video haben Vorrang
          </div>
          {/* 🆕 FOV-Freitext: manuelle Gradzahl schlägt Profil UND EXIF */}
          <div className="mt-3">
            <Label className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
              FOV manuell (°) · optional
            </Label>
            <input
              data-testid="cfg-camera-fov-override"
              type="number"
              min="20"
              max="160"
              step="0.1"
              value={fovOverride}
              onChange={(e) => setFovOverride(e.target.value)}
              disabled={configLocked}
              placeholder="z. B. 42.5 — leer = Profil/EXIF nutzen"
              className="mt-1 w-full bg-secondary border border-border rounded-sm px-2 py-1.5 text-sm font-mono focus:border-primary focus:outline-none disabled:opacity-50"
            />
            <div className="text-[10px] text-muted-foreground mt-1 font-mono uppercase tracking-widest">
              Eingetippte Gradzahl schlägt Profil UND EXIF (20–160°)
            </div>
          </div>
          {/* 🆕 Phase 4 · Fremd-Mittellinie sichtbar (aux_midline) */}
          <div className="mt-4 flex items-start gap-2.5">
            <Checkbox
              id="cfg-aux-midline"
              data-testid="cfg-aux-midline"
              checked={auxMidline}
              onCheckedChange={(v) => setAuxMidline(!!v)}
              disabled={configLocked}
              className="mt-0.5"
            />
            <div>
              <label
                htmlFor="cfg-aux-midline"
                className="font-mono text-[11px] uppercase tracking-widest cursor-pointer"
              >
                Fremd-Mittellinie sichtbar
              </label>
              <div className="text-[10px] text-muted-foreground mt-0.5 font-mono uppercase tracking-widest">
                Schaltet im Anker-Sammelmodus die Zeichen-Klasse „Mittellinie" (M) als zusätzliche Solver-Constraint frei
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dropzone + status */}
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <div
            data-testid={TID.uploadDropzone}
            onDragOver={(e) => e.preventDefault()}
            onDrop={canPickAnother ? onDrop : undefined}
            onClick={() => canPickAnother && inputRef.current?.click()}
            className={`tactical-grid border border-dashed rounded-sm p-10 min-h-[340px] flex flex-col items-center justify-center transition-colors group ${
              canPickAnother
                ? "border-border hover:border-primary/60 cursor-pointer"
                : "border-primary/40 cursor-default"
            }`}
          >
            <input
              data-testid={TID.uploadInput}
              ref={inputRef}
              type="file"
              accept="video/mp4,video/quicktime,.mp4,.mov,.m4v,.webm"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {!file ? (
              <>
                <Upload className="w-12 h-12 text-muted-foreground mb-3 group-hover:text-primary transition-colors" strokeWidth={1.5} />
                <div className="font-display font-black uppercase tracking-wider text-base mb-1.5">
                  Drop video here
                </div>
                <div className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
                  .mp4 · .mov · up to {MAX_UPLOAD_GB} GB · direct upload to Backblaze
                </div>
              </>
            ) : (
              <>
                <FileVideo className="w-12 h-12 text-primary mb-3" strokeWidth={1.5} />
                <div data-testid={TID.uploadFilename} className="font-mono text-sm text-foreground mb-1 text-center break-all px-4">
                  {file.name}
                </div>
                <div className="font-mono text-xs text-muted-foreground">
                  {(file.size / (1024 * 1024)).toFixed(1)} MB
                </div>
                {/* 🆕 Auftrag 60, R4 · Hinweis zur Namensregel, nur hinter dem Schalter der Klickstrecke (keine Sperre). */}
                {BODENKLICK_AN && namensHinweisText && (
                  <div data-testid="upload-namens-hinweis"
                       className="mt-2 px-4 font-mono text-[11px] leading-snug text-amber-300 text-center">
                    {namensHinweisText}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Phase indicator */}
          {phase === "uploading" && (
            <div className="mt-4 border border-border bg-card rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span className="font-mono text-xs uppercase tracking-widest text-foreground">
                  Uploading to Backblaze… {progress}%
                </span>
              </div>
              <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
          {phase === "thumbnailing" && (
            <div className="mt-4 border border-primary/40 bg-card rounded-sm p-4 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="font-mono text-xs uppercase tracking-widest text-foreground">
                Decoding thumbnail (ffmpeg, codec-safe)…
              </span>
            </div>
          )}
          {phase === "ready" && (
            <div className="mt-4 border border-primary bg-primary/10 rounded-sm p-4 flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />
              <span className="font-mono text-xs uppercase tracking-widest text-primary">
                Upload complete · thumbnail ready
              </span>
            </div>
          )}
          {phase === "error" && (
            <div className="mt-4 border border-destructive/60 bg-destructive/10 rounded-sm p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className="font-mono text-xs uppercase tracking-widest text-destructive">
                  Upload failed
                </span>
                {uploadFehler && (
                  <div
                    data-testid="upload-error-detail"
                    className="mt-1.5 font-mono text-[11px] leading-snug text-destructive/90 break-words whitespace-pre-wrap"
                  >
                    {uploadFehler}
                  </div>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={reset}>Try another file</Button>
            </div>
          )}
        </div>

        {/* Thumbnail preview / Continue */}
        <div className="lg:col-span-2 space-y-3">
          <div className="border border-border bg-card rounded-sm p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
              <ImageIcon className="w-3 h-3" /> Codec-safe Preview
            </div>
            <div className="aspect-video bg-secondary rounded-sm overflow-hidden flex items-center justify-center">
              {thumbBlobUrl ? (
                <img
                  data-testid="step1-thumbnail-preview"
                  src={thumbBlobUrl}
                  alt="thumbnail"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {phase === "idle" ? "Awaiting file" : phase === "ready" ? "No thumbnail" : "Pending…"}
                </div>
              )}
            </div>
            <div className="mt-3 font-mono text-[10px] text-muted-foreground leading-relaxed">
              Extracted via <span className="text-primary">ffmpeg</span> from the B2 source — works with H.264, HEVC (iPhone), VP9.
            </div>
          </div>

          <Button
            data-testid={TID.createSequenceBtn}
            onClick={goToCalibration}
            disabled={phase !== "ready"}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest disabled:opacity-40"
          >
            {phase === "ready" ? "Continue to Calibration →" : "Waiting…"}
          </Button>
          {phase !== "idle" && phase !== "uploading" && phase !== "thumbnailing" && (
            <Button variant="outline" onClick={reset} className="w-full border-border bg-secondary hover:bg-accent text-foreground">
              Reset & pick another file
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
